/**
 * CrystalField — layered triangle-grid shimmer with floating diamonds,
 * a rotating central hexagon, orbiting triangles, and a radial glow.
 *
 * Extracted from the "Trinity Basics" card preview in playground-main.ts so
 * it can be dropped on any <canvas> at any size.
 *
 *   const field = new CrystalField(canvas);
 *   field.start();
 *   // later:
 *   field.destroy();
 */

import { TRINITY_PALETTE } from './types';

export interface CrystalFieldDiamond {
  /** Horizontal offset from canvas center, as a fraction of width (-0.5..0.5). */
  x: number;
  /** Vertical offset from canvas center, as a fraction of height (-0.5..0.5). */
  y: number;
  /** Diamond half-width in px. */
  size: number;
  /** Float speed multiplier. */
  speed: number;
  /** Phase offset (radians). */
  phase: number;
  /** Index into the color palette. */
  color: number;
}

export interface CrystalFieldConfig {
  /** Triangle palette. Defaults to Trinity blues (light, medium, dark, deep). */
  colors?: readonly string[];
  /**
   * Background fill. `"gradient"` paints the original dark blue gradient,
   * a CSS color paints a flat color, `null` leaves the canvas transparent.
   */
  background?: 'gradient' | string | null;
  /** Grid triangle side length in px. Default 14. */
  gridSize?: number;
  /** Central hexagon radius in px. Default 26. */
  hexRadius?: number;
  /** Orbit ring radius in px. Default 50. */
  orbitRadius?: number;
  /** Number of orbiting triangles. Default 5. */
  orbitCount?: number;
  /** Global time multiplier (1 = original speed). */
  speed?: number;
  /** Override the default floating diamond layout. */
  diamonds?: readonly CrystalFieldDiamond[];
  /** Max device pixel ratio to honor. Default 2. */
  maxDpr?: number;
}

const DEFAULT_DIAMONDS: readonly CrystalFieldDiamond[] = [
  { x: -0.28, y: -0.2,  size: 18, speed: 0.7, phase: 0,   color: 0 },
  { x:  0.3,  y: -0.25, size: 14, speed: 0.9, phase: 1.2, color: 1 },
  { x: -0.35, y:  0.22, size: 16, speed: 0.5, phase: 2.4, color: 2 },
  { x:  0.32, y:  0.28, size: 12, speed: 1.1, phase: 3.6, color: 3 },
  { x: -0.1,  y:  0.35, size: 10, speed: 0.8, phase: 4.8, color: 0 },
  { x:  0.15, y: -0.38, size: 11, speed: 0.6, phase: 0.8, color: 1 },
  { x: -0.38, y: -0.05, size:  9, speed: 1.3, phase: 2.0, color: 2 },
  { x:  0.38, y:  0.05, size: 13, speed: 0.4, phase: 5.2, color: 3 },
  { x:  0.0,  y: -0.35, size:  8, speed: 1.0, phase: 1.6, color: 0 },
  { x: -0.2,  y:  0.1,  size: 10, speed: 0.7, phase: 3.0, color: 1 },
  { x:  0.22, y:  0.12, size: 11, speed: 0.9, phase: 4.0, color: 2 },
];

const DEFAULT_COLORS: readonly string[] = [
  TRINITY_PALETTE.lightBlue,
  TRINITY_PALETTE.mediumBlue,
  TRINITY_PALETTE.darkBlue,
  TRINITY_PALETTE.deepBlue,
];

function hash(a: number, b: number): number {
  const x = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return x - Math.floor(x);
}

export class CrystalField {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly colors: readonly string[];
  private readonly background: 'gradient' | string | null;
  private readonly gridSize: number;
  private readonly hexRadius: number;
  private readonly orbitRadius: number;
  private readonly orbitCount: number;
  private readonly speed: number;
  private readonly diamonds: readonly CrystalFieldDiamond[];
  private readonly maxDpr: number;

  private animationId: number | null = null;
  private startTime = 0;
  private resizeObserver: ResizeObserver | null = null;
  private cssWidth = 0;
  private cssHeight = 0;

  constructor(canvas: HTMLCanvasElement, config: CrystalFieldConfig = {}) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('CrystalField: failed to acquire 2D context');

    this.canvas = canvas;
    this.ctx = ctx;
    this.colors = config.colors ?? DEFAULT_COLORS;
    this.background = config.background ?? 'gradient';
    this.gridSize = config.gridSize ?? 14;
    this.hexRadius = config.hexRadius ?? 26;
    this.orbitRadius = config.orbitRadius ?? 50;
    this.orbitCount = config.orbitCount ?? 5;
    this.speed = config.speed ?? 1;
    this.diamonds = config.diamonds ?? DEFAULT_DIAMONDS;
    this.maxDpr = config.maxDpr ?? 2;

    this.resize();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(canvas);
    }
  }

  start(): void {
    if (this.animationId !== null) return;
    this.startTime = performance.now();

    const tick = () => {
      const t = ((performance.now() - this.startTime) / 1000) * this.speed;
      this.render(t);
      this.animationId = requestAnimationFrame(tick);
    };
    tick();
  }

  stop(): void {
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  destroy(): void {
    this.stop();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  private resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, this.maxDpr);
    this.cssWidth = rect.width;
    this.cssHeight = rect.height;
    this.canvas.width = Math.max(1, Math.round(rect.width * dpr));
    this.canvas.height = Math.max(1, Math.round(rect.height * dpr));
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private render(time: number): void {
    const { ctx, colors } = this;
    const w = this.cssWidth;
    const h = this.cssHeight;
    if (w === 0 || h === 0) return;

    // --- Background ---
    if (this.background === 'gradient') {
      const bg = ctx.createLinearGradient(0, 0, w, h);
      bg.addColorStop(0, '#080d18');
      bg.addColorStop(0.5, '#0c1222');
      bg.addColorStop(1, '#0a0f1a');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);
    } else if (typeof this.background === 'string') {
      ctx.fillStyle = this.background;
      ctx.fillRect(0, 0, w, h);
    } else {
      ctx.clearRect(0, 0, w, h);
    }

    const cx = w / 2;
    const cy = h / 2;

    // --- Layer 1: Background triangle grid with wave shimmer ---
    const gs = this.gridSize;
    const gh = (gs * Math.sqrt(3)) / 2;
    const gridCols = Math.ceil(w / (gs / 2)) + 4;
    const gridRows = Math.ceil(h / gh) + 4;
    const ox = -gs;
    const oy = -gh;

    for (let r = 0; r < gridRows; r++) {
      for (let c = 0; c < gridCols; c++) {
        const isUp = (r + c) % 2 === 0;
        const gx = ox + c * (gs / 2);
        const gy = oy + r * gh;

        const dx = (gx + gs / 2 - cx) / w;
        const dy = (gy + gh / 2 - cy) / h;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const wave = Math.sin(dist * 12 - time * 1.5) * 0.5 + 0.5;
        const flicker = hash(r, c);

        if (wave * flicker < 0.25) continue;

        const colorIdx = (r + c + Math.floor(flicker * 4)) % colors.length;
        ctx.fillStyle = colors[colorIdx];
        ctx.globalAlpha = 0.06 + wave * flicker * 0.12;

        ctx.beginPath();
        if (isUp) {
          ctx.moveTo(gx + gs / 2, gy);
          ctx.lineTo(gx, gy + gh);
          ctx.lineTo(gx + gs, gy + gh);
        } else {
          ctx.moveTo(gx + gs / 2, gy + gh);
          ctx.lineTo(gx + gs, gy);
          ctx.lineTo(gx, gy);
        }
        ctx.closePath();
        ctx.fill();
      }
    }

    // --- Layer 2: Floating diamond crystals ---
    for (const d of this.diamonds) {
      const dx = cx + d.x * w + Math.sin(time * d.speed + d.phase) * 6;
      const dy = cy + d.y * h + Math.cos(time * d.speed * 0.7 + d.phase) * 4;
      const s = d.size;
      const dh = (s * Math.sqrt(3)) / 2;
      const pulse = 0.5 + Math.sin(time * 1.5 + d.phase) * 0.3;

      ctx.fillStyle = colors[d.color % colors.length];
      ctx.globalAlpha = 0.35 + pulse * 0.35;
      ctx.beginPath();
      ctx.moveTo(dx, dy - dh);
      ctx.lineTo(dx - s / 2, dy);
      ctx.lineTo(dx, dy + dh);
      ctx.lineTo(dx + s / 2, dy);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = colors[(d.color + 1) % colors.length];
      ctx.globalAlpha = 0.15 + pulse * 0.2;
      ctx.beginPath();
      ctx.moveTo(dx, dy - dh);
      ctx.lineTo(dx + s / 2, dy);
      ctx.lineTo(dx, dy + dh);
      ctx.closePath();
      ctx.fill();

      ctx.globalAlpha = pulse * 0.25;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(dx, dy - dh * 0.4, 1.5 + pulse, 0, Math.PI * 2);
      ctx.fill();
    }

    // --- Layer 3: Central hexagon (slow rotation, faceted) ---
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(time * 0.12);
    const hexR = this.hexRadius;
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      const nextAngle = (Math.PI / 3) * (i + 1) - Math.PI / 2;
      const facePulse = Math.sin(time * 2 + i * 1.05);
      ctx.fillStyle = colors[i % colors.length];
      ctx.globalAlpha = 0.5 + facePulse * 0.3;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(angle) * hexR, Math.sin(angle) * hexR);
      ctx.lineTo(Math.cos(nextAngle) * hexR, Math.sin(nextAngle) * hexR);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = colors[(i + 1) % colors.length];
      ctx.globalAlpha = 0.2 + facePulse * 0.15;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * hexR, Math.sin(angle) * hexR);
      ctx.lineTo(Math.cos(nextAngle) * hexR, Math.sin(nextAngle) * hexR);
      ctx.stroke();
    }
    ctx.restore();

    // --- Layer 4: Orbiting triangles around hexagon ---
    const orbitR = this.orbitRadius;
    const orbitCount = this.orbitCount;
    for (let i = 0; i < orbitCount; i++) {
      const angle = ((Math.PI * 2) / orbitCount) * i + time * 0.35;
      const ox2 = cx + Math.cos(angle) * orbitR;
      const oy2 = cy + Math.sin(angle) * orbitR;
      const triSize = 8 + Math.sin(time * 2 + i) * 2;
      const triH = (triSize * Math.sqrt(3)) / 2;

      ctx.fillStyle = colors[i % colors.length];
      ctx.globalAlpha = 0.6 + Math.sin(time * 3 + i * 1.3) * 0.3;
      ctx.save();
      ctx.translate(ox2, oy2);
      ctx.rotate(angle + time * 0.5);
      ctx.beginPath();
      ctx.moveTo(0, -triH * 0.67);
      ctx.lineTo(-triSize / 2, triH * 0.33);
      ctx.lineTo(triSize / 2, triH * 0.33);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    // --- Layer 5: Subtle radial glow from center ---
    const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, w * 0.45);
    glow.addColorStop(0, 'rgba(27, 177, 231, 0.08)');
    glow.addColorStop(0.5, 'rgba(0, 96, 175, 0.03)');
    glow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.globalAlpha = 0.6 + Math.sin(time * 0.8) * 0.2;
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);

    ctx.globalAlpha = 1;
  }
}
